import { useMemo, useCallback, useState } from "react";
import { useAccountant } from "./AccountantContext";
import FilterBar from "./FilterBar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import { getWorkOrders } from "@/service/PricingService";
import * as XLSX from "xlsx";

const COLUMNS = [
    { key: 'cos_job_id', label: 'Job Id' },
    { key: 'full_address', label: 'Address', minWidth: 180 },
    { key: 'agent_name', label: 'Agent Name' },
    { key: 'room_info', label: 'Room Info' },
    { key: 'job_date', label: 'Job Date' },
    { key: 'worker', label: 'Worker' },
    { key: 'invoice', label: 'Invoice' },
    { key: 'agent_total_price', label: 'Agent Total Price' },
    { key: 'cleaner_labour', label: 'Cleaner Labour' },
    { key: 'parking', label: 'Parking fee' },
    { key: 'extra', label: 'Extra Cleaner labour' },
    { key: 'extra_cleaning_notes', label: 'Extra Cleaning Notes', minWidth: 180 },
    { key: 'comments', label: 'Comments', minWidth: 180 },
];

const headCellSx = { fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13, fontStyle: 'italic' };
const bodyCellSx = { fontSize: 13, py: 0.5, whiteSpace: 'nowrap' };
const truncatedCellSx = { ...bodyCellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'clip' };
const agentCellSx = { ...bodyCellSx, maxWidth: 120, overflow: 'hidden', textOverflow: 'clip' };
const TRUNCATED_COLS = { full_address: truncatedCellSx, agent_name: agentCellSx };
const paginationSx = { '& .MuiTablePagination-toolbar': { alignItems: 'baseline' } };

export default function WorkOrderTab() {
    const [state, dispatch] = useAccountant();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const filteredRows = useMemo(() => {
        return state.workOrder
            .filter(it => it.full_address?.toLowerCase().includes(state.searchAddress.toLowerCase()))
            .filter(it => !state.searchJobId || String(it.cos_job_id).includes(String(state.searchJobId)))
            .filter(it => it.agent_name ? it.agent_name.toLowerCase().includes(state.searchAgent.toLowerCase()) : state.searchAgent === '');
    }, [state.workOrder, state.searchAddress, state.searchJobId, state.searchAgent]);

    const refreshWorkOrders = useCallback(() => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        getWorkOrders(state.filterStartDate, state.filterEndDate)
            .then(res => { if (!res.ok) throw new Error('Failed to load work orders'); return res.json(); })
            .then(data => {
                dispatch({ type: 'SET_WORK_ORDERS', payload: data });
                dispatch({ type: 'SET_LOADING', payload: false });
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch, state.filterStartDate, state.filterEndDate]);

    const handleExport = useCallback(() => {
        const exportData = filteredRows.map(row => ({
            ...row,
            linens_price: row.linens_price !== undefined ? row.linens_price : '',
            consumable_price: row.consumable_price !== undefined ? row.consumable_price : '',
            extra_cleaning_notes: row.extra_cleaning_notes !== undefined ? row.extra_cleaning_notes : '',
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Work Orders');
        XLSX.writeFile(wb, 'work_orders.xlsx');
    }, [filteredRows]);

    return (
        <div>
            <FilterBar showJobIdFilter showExportButton onRefresh={refreshWorkOrders} onExport={handleExport} />

            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map(col => (
                                <TableCell key={col.key} sx={{ ...headCellSx, ...(col.minWidth ? { minWidth: col.minWidth } : {}) }}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                            <TableRow key={`${row.cos_job_id}-${idx}`} hover>
                                {COLUMNS.map(col => (
                                    <TableCell key={col.key} sx={TRUNCATED_COLS[col.key] || bodyCellSx} title={TRUNCATED_COLS[col.key] ? row[col.key] : undefined}>{row[col.key]}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {filteredRows.length === 0 && (
                            <TableRow><TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data to display</TableCell></TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={filteredRows.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[20, 50, 100]}
                                labelDisplayedRows={({ from, to, count }) => `${count} total`}
                                sx={paginationSx}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </div>
    );
}
